<script setup>
import { Head, router } from '@inertiajs/vue3';
import { useKaleidicalStore } from '@/stores/kaleidical';
import { storeToRefs } from 'pinia';
import { inject, ref } from 'vue';
import Kaledical from '@/components/Kaledical.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { all } = storeToRefs(useKaleidicalStore());

const modals = inject('modals');

const accessKaleidical = (kaleidical) => {
    router.visit(`/kaleidical/${kaleidical.hash}/items`);
};

const { archive, update } = useKaleidicalStore();

const label = ref('');
const editMode = ref(false);
const editIndex = ref(null);

const save = (kaleidical) => {
    update({
        hash: kaleidical.hash,
        name: label.value
    }).then(() => {
        editMode.value = false;
        label.value = '';
        editIndex.value = null;
    });
};

const archiveKaleidical = async (kaleidical) => {
    try {
        await archive(kaleidical);

    } catch (error) {
        console.error(error);
    }
};

const openModal = (modal, mode = 'Create', kaleidical = null) => {
    modals.show(modal, {
        props: kaleidical ? {
            kaleidical: kaleidical,
            mode: mode
        } : {
            mode: mode,
        }
    });
};

const enableEdit = (name, index) => {
    editMode.value = true;
    label.value = name;
    editIndex.value = index;
};

</script>
<template>
<MainContentLayout>
    <Head title="Kaleidicals" />
    <template #breadcrumb>
        <BreadCrumb />
    </template>
    <template #title>
        Kaleidicals
    </template>
    <template #actions>
        <div class="flex gap-2 items-center">
            <div v-tooltip="'Add New'" class="rounded-full h-10 w-10 flex items-center cursor-pointer justify-center bg-primary hover:bg-primary-light mr-4" @click="openModal('KaleidicalModal')">
                <FontAwesomeIcon icon="plus" class="text-base" />
            </div>
            <div v-tooltip="'Accept Invite'" class="rounded-full h-10 w-10 flex items-center cursor-pointer justify-center bg-secondary hover:bg-primary-light" @click="openModal('AcceptInviteModal')">
                <FontAwesomeIcon icon="calendar-users" class="text-base" />
            </div>
        </div>
    </template>

    <main v-for="(kaleidical, index) in all" :key="kaleidical.id" class="py-6 px-4 w-full">
        <div class="relative border rounded-xl bg-grey-100">
            <div class="absolute top-3 left-5 z-10">
                <h1 v-if="editIndex !== index" class="cursor-pointer flex gap-2 mt-1 text-lg font-semibold text-primary" @click="enableEdit(kaleidical.name, index)">{{ kaleidical.name }} <div class="cursor-pointer"><FontAwesomeIcon icon="pencil" /></div></h1>
                <template v-if="editIndex === index">
                    <div class="flex gap-2">
                        <StyledInput
                            v-model="label"
                            class="w-60"
                            density="compact"
                            @blur="save(kaleidical)" />
                        <div class="rounded-full h-10 w-10 flex mb-5 items-center cursor-pointer justify-center bg-green-300 hover:bg-primary-light mr-4 z-10" @click="save(kaleidical)">
                            <FontAwesomeIcon icon="save" class="text-base cursor-pointer" />
                        </div>
                    </div>
                </template>
            </div>
            <div class="absolute top-5 right-1 rounded-full h-10 w-10 flex items-center cursor-pointer justify-center bg-primary hover:bg-primary-light mr-4 z-10" @click="openModal('InviteModal', 'Create', kaleidical)">
                <FontAwesomeIcon icon="user-plus" class="text-base cursor-pointer" />
            </div>
            <div class="w-full flex justify-center opacity-50">
                <Kaledical
                    :id="kaleidical.id"
                    class="fade-in-out"
                    :items="kaleidical.items" />
            </div>
            <div class="mt-4 flex justify-center absolute right-5 bottom-5 cursor-pointer z-10"  @click="accessKaleidical(kaleidical)">
                <div class="text-primary flex gap-4 items-center mr-2">
                    <h6>View</h6>
                    <FontAwesomeIcon icon="chevron-right" class="text-3xl" />
                </div>
            </div>

            <div class="mt-4 flex justify-center cursor-pointer absolute left-5 bottom-5 z-10">
                <StyledButton color="info" size="small" @click.stop="archiveKaleidical(kaleidical)">
                    Archive <FontAwesomeIcon icon="box-archive" class="ml-3" />
                </StyledButton>
            </div>
        </div>
    </main>
</MainContentLayout>
</template>
<style scoped>
.fade-in-out {
  transition: opacity 1s ease-in-out;
  opacity: 1;
}
</style>
